import { Archive, Copy, Pencil, Plus, RotateCcw, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTransitionNavigate } from "../hooks/useTransitionNavigate";
import { getApiErrorMessage } from "../lib/api-error";
import { TRAINING_PLANS_SECTION_ID } from "../lib/scroll-intent";
import {
  buildPlanUsageCounts,
  filterClientPlans,
  getRecentlyUsedPlanIds,
  groupPlansByMonth,
  sliceVisiblePlans,
  type TrainingPlanTab,
} from "../lib/training-plan-utils";
import {
  useDeleteTrainingPlanMutation,
  useGetBookingsQuery,
  useGetTrainingPlansQuery,
  useUpdateTrainingPlanMutation,
} from "../store/coachApi";
import ErrorAlert from "./ErrorAlert";

interface TrainingPlansPanelProps {
  clientId: string;
}

const TrainingPlansPanel = ({ clientId }: TrainingPlansPanelProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useTransitionNavigate();
  const { data: allPlans = [], isLoading, error: fetchError, refetch } =
    useGetTrainingPlansQuery();
  const { data: allBookings = [] } = useGetBookingsQuery();
  const [updateTrainingPlan] = useUpdateTrainingPlanMutation();
  const [deleteTrainingPlan] = useDeleteTrainingPlanMutation();

  const [tab, setTab] = useState<TrainingPlanTab>("active");
  const [expanded, setExpanded] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const usageCounts = useMemo(
    () => buildPlanUsageCounts(allBookings),
    [allBookings],
  );

  const recentlyUsedIds = useMemo(
    () => new Set(getRecentlyUsedPlanIds(allBookings, clientId)),
    [allBookings, clientId],
  );

  const statusFilter = tab === "active" ? "ACTIVE" : "ARCHIVED";
  const clientPlans = useMemo(
    () => filterClientPlans(allPlans, clientId, statusFilter),
    [allPlans, clientId, statusFilter],
  );

  const { visible: visiblePlans, hiddenCount } = useMemo(
    () => sliceVisiblePlans(clientPlans, expanded),
    [clientPlans, expanded],
  );

  const monthGroups = useMemo(
    () => groupPlansByMonth(visiblePlans, i18n.language),
    [visiblePlans, i18n.language],
  );

  const activeCount = useMemo(
    () => filterClientPlans(allPlans, clientId, "ACTIVE").length,
    [allPlans, clientId],
  );

  const archivedCount = useMemo(
    () => filterClientPlans(allPlans, clientId, "ARCHIVED").length,
    [allPlans, clientId],
  );

  const loadError = fetchError
    ? getApiErrorMessage(fetchError, "Не удалось загрузить данные")
    : null;

  const handleDuplicate = (planId: string) => {
    navigate("plans/new", { state: { copyFromPlanId: planId } });
  };

  const handleArchive = async (planId: string) => {
    setActionId(planId);
    setError(null);
    try {
      await updateTrainingPlan({
        id: planId,
        data: { status: "ARCHIVED" },
      }).unwrap();
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось архивировать план"));
    } finally {
      setActionId(null);
    }
  };

  const handleRestore = async (planId: string) => {
    setActionId(planId);
    setError(null);
    try {
      await updateTrainingPlan({
        id: planId,
        data: { status: "ACTIVE" },
      }).unwrap();
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось восстановить план"));
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async (planId: string) => {
    if (!confirm(t("Вы уверены, что хотите удалить этот план?"))) {
      return;
    }

    setActionId(planId);
    setError(null);
    try {
      await deleteTrainingPlan(planId).unwrap();
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось удалить план"));
    } finally {
      setActionId(null);
    }
  };

  const renderPlanMeta = (planId: string, exerciseCount?: number) => {
    const usage = usageCounts.get(planId) ?? 0;
    const parts: string[] = [];

    if (exerciseCount !== undefined) {
      parts.push(t("{{count}} упр.", { count: exerciseCount }));
    }

    if (usage > 0) {
      parts.push(t("{{count}} тренировок", { count: usage }));
    }

    return parts.join(" · ");
  };

  const renderPlanCard = (plan: (typeof clientPlans)[number]) => {
    const isBusy = actionId === plan.id;
    const isArchived = plan.status === "ARCHIVED";
    const isRecent = recentlyUsedIds.has(plan.id);

    return (
      <div
        key={plan.id}
        className="p-4 rounded-2xl border border-slate-100 bg-white hover:border-slate-200 transition-colors"
      >
        <div className="flex items-start justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate(`plans/${plan.id}/edit`)}
            className="min-w-0 flex-1 text-left"
          >
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h3 className="font-bold text-slate-900 truncate">{plan.name}</h3>
              {isRecent && tab === "active" && (
                <span className="badge bg-primary-soft text-primary">
                  {t("Недавний")}
                </span>
              )}
              {isArchived && (
                <span className="badge bg-slate-100 text-slate-600">
                  {t("В архиве")}
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500">
              {new Date(plan.createdAt).toLocaleDateString(undefined, {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
              {renderPlanMeta(plan.id, plan.exerciseCount)
                ? ` · ${renderPlanMeta(plan.id, plan.exerciseCount)}`
                : ""}
            </p>
          </button>

          <div className="flex items-center gap-1 shrink-0">
            {tab === "active" ? (
              <>
                <button
                  type="button"
                  onClick={() => handleDuplicate(plan.id)}
                  className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400 hover:text-primary"
                  title={t("Скопировать для новой тренировки")}
                >
                  <Copy size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`plans/${plan.id}/edit`)}
                  className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400 hover:text-primary"
                  title={t("Редактировать")}
                >
                  <Pencil size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => void handleArchive(plan.id)}
                  disabled={isBusy}
                  className="p-2 hover:bg-amber-50 rounded-xl transition-colors text-slate-400 hover:text-amber-600 disabled:opacity-50"
                  title={t("В архив")}
                >
                  <Archive size={18} />
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => void handleRestore(plan.id)}
                  disabled={isBusy}
                  className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400 hover:text-primary disabled:opacity-50"
                  title={t("Восстановить")}
                >
                  <RotateCcw size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`plans/${plan.id}/edit`)}
                  className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400 hover:text-primary"
                  title={t("Просмотреть")}
                >
                  <Pencil size={18} />
                </button>
                {(usageCounts.get(plan.id) ?? 0) === 0 && (
                  <button
                    type="button"
                    onClick={() => void handleDelete(plan.id)}
                    disabled={isBusy}
                    className="p-2 hover:bg-red-50 rounded-xl transition-colors text-slate-400 hover:text-red-500 disabled:opacity-50"
                    title={t("Удалить")}
                  >
                    {isBusy ? (
                      <div className="w-4 h-4 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
                    ) : (
                      <Trash2 size={18} />
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div id={TRAINING_PLANS_SECTION_ID} className="card p-5 md:p-6 scroll-mt-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-slate-900">
          {t("Планы тренировок")}
        </h2>
        <button
          type="button"
          onClick={() => navigate("plans/new")}
          className="btn-primary text-sm py-2 w-full sm:w-auto shrink-0"
        >
          <Plus size={16} />
          <span>{t("План")}</span>
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        <button
          type="button"
          onClick={() => {
            setTab("active");
            setExpanded(false);
          }}
          className={`text-sm font-semibold px-3 py-1.5 rounded-xl transition-colors ${
            tab === "active"
              ? "bg-primary-soft text-primary"
              : "text-slate-500 hover:bg-slate-100"
          }`}
        >
          {t("Активные")}
          {activeCount > 0 ? ` (${activeCount})` : ""}
        </button>
        <button
          type="button"
          onClick={() => {
            setTab("archived");
            setExpanded(false);
          }}
          className={`text-sm font-semibold px-3 py-1.5 rounded-xl transition-colors ${
            tab === "archived"
              ? "bg-slate-200 text-slate-700"
              : "text-slate-500 hover:bg-slate-100"
          }`}
        >
          {t("Архив")}
          {archivedCount > 0 ? ` (${archivedCount})` : ""}
        </button>
      </div>

      {(error || loadError) && (
        <ErrorAlert
          message={error || loadError!}
          onRetry={() => void refetch()}
          onDismiss={() => setError(null)}
          className="mb-4"
        />
      )}

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="spinner h-8 w-8" />
        </div>
      ) : clientPlans.length === 0 ? (
        <div className="empty-state py-8 px-4">
          <p className="text-sm text-slate-500 font-medium">
            {tab === "active"
              ? t("Нет активных планов")
              : t("Архив пуст")}
          </p>
          {tab === "active" && (
            <>
              <p className="text-xs text-slate-400 mt-1 mb-4">
                {t("Создайте программу упражнений и назначьте её при записи на тренировку")}
              </p>
              <button
                type="button"
                onClick={() => navigate("plans/new")}
                className="btn-primary text-sm py-2 min-h-11"
              >
                <Plus size={16} />
                {t("Создать первый план")}
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {monthGroups.map((group) => (
            <div key={group.key}>
              <p className="section-label mb-2">
                {group.label}
              </p>
              <div className="space-y-3">
                {group.plans.map((plan) => renderPlanCard(plan))}
              </div>
            </div>
          ))}

          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="btn-secondary w-full text-sm py-2"
            >
              {t("Показать ещё {{count}}", { count: hiddenCount })}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TrainingPlansPanel;
